import React, { useEffect, useMemo, useState } from 'react'
import {
    Card, Row, Col, Input, InputNumber, DatePicker, Select, Button, Table,
    Typography, message, Popconfirm, Space, Divider, Switch, Empty, AutoComplete, Drawer, Tooltip
} from 'antd'
import { PlusOutlined, DeleteOutlined, SaveOutlined, FileExcelOutlined, EditOutlined } from '@ant-design/icons'
import moment from 'moment'
import * as XLSX from 'xlsx'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { GET, POST } from '../../utils/apis'
import { secureApi } from '../../Config/api'
import { computeBudget, buildSchedule } from '../../utils/budgetCalc'
import { unitLibrary } from '../../utils/unitLibrary'
import { THEME_COLOR } from '../../utils/constants'
import { verifyToken } from '../../utils/helpers'

const { Title, Text } = Typography

// Small self-contained labels (IT default / EN)
const L = {
    it: {
        title: 'Stima del budget', subtitle: 'Definizione del budget di progetto',
        projects: 'Progetti', newProject: 'Nuovo progetto', save: 'Salva', del: 'Elimina',
        params: 'Parametri progetto', name: 'Nome progetto', totalBuses: 'Totale autobus',
        busesDone: 'Autobus già completati', startDate: 'Data inizio',
        targetMonFri: 'Target giornaliero (Lun-Ven)', targetSat: 'Target Sabato',
        workHolidays: 'Lavora nei festivi',
        costs: 'Costi unitari (per persona / giorno)', costMeal: 'Vitto', costLodging: 'Alloggio',
        costExternal: 'Costo risorsa esterna (giorno)', employeeDaily: 'Costo dipendente (giorno)',
        team: 'Squadra', numEmployees: 'N. dipendenti', numExternals: 'N. risorse esterne',
        materials: 'Materiali', flights: 'Voli', hotels: 'Hotel', addRow: 'Aggiungi',
        type: 'Tipologia', unitPrice: 'Prezzo unitario', qty: 'Quantità', vat: 'IVA %',
        note: 'Note', person: 'Nome', route: 'Tratta', amount: 'Importo (€)', status: 'Stato',
        arrival: 'Data arrivo', nights: 'Notti', summary: 'Riepilogo budget',
        remaining: 'Autobus residui', workingDays: 'Giorni lavorativi', completion: 'Fine prevista',
        labor: 'Costo manodopera', matTotal: 'Materiali (IVA incl.)', travel: 'Viaggi',
        grand: 'TOTALE BUDGET', notCompletable: 'Non calcolabile (imposta target/data)',
        exportExcel: 'Esporta Excel',
        days: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
        dayTypes: { working: 'Lavorativo', saturday: 'Sabato', sunday: 'Domenica' },
        shParam: 'Parametro', shValue: 'Valore', shRiepilogo: 'Riepilogo', shPiano: 'Piano',
        shDate: 'Data', shDay: 'Giorno', shType: 'Tipo', shTarget: 'Target', shProgr: 'Progressivo', shResidual: 'Residuo',
        shNet: 'Netto (€)', shVatAmt: 'IVA (€)', shTotal: 'Totale (€)', shTotali: 'TOTALI',
        savedEstimates: 'Stime salvate', load: 'Apri', download: 'Scarica Excel', noSaved: 'Nessuna stima salvata',
        flightStatuses: ['Pagato', 'Da richiedere', 'Da pagare', 'Rimborsato', 'Prenotato'],
        add: 'Aggiungi', actions: 'Azioni', cancel: 'Annulla', confirmDelete: 'Eliminare questa stima?',
        newTitle: 'Nuova stima', editTitle: 'Modifica stima', selectDate: 'Seleziona data', noData: 'Nessun dato',
    },
    en: {
        title: 'Budget Estimation', subtitle: 'Project budget definition',
        projects: 'Projects', newProject: 'New project', save: 'Save', del: 'Delete',
        params: 'Project parameters', name: 'Project name', totalBuses: 'Total buses',
        busesDone: 'Buses already completed', startDate: 'Start date',
        targetMonFri: 'Daily target (Mon-Fri)', targetSat: 'Saturday target',
        workHolidays: 'Work on holidays',
        costs: 'Unit costs (per person / day)', costMeal: 'Meals', costLodging: 'Lodging',
        costExternal: 'External resource cost (day)', employeeDaily: 'Employee cost (day)',
        team: 'Team', numEmployees: 'Employees', numExternals: 'External resources',
        materials: 'Materials', flights: 'Flights', hotels: 'Hotels', addRow: 'Add',
        type: 'Type', unitPrice: 'Unit price', qty: 'Quantity', vat: 'VAT %',
        note: 'Note', person: 'Name', route: 'Route', amount: 'Amount (€)', status: 'Status',
        arrival: 'Arrival date', nights: 'Nights', summary: 'Budget summary',
        remaining: 'Remaining buses', workingDays: 'Working days', completion: 'Projected end',
        labor: 'Labour cost', matTotal: 'Materials (incl. VAT)', travel: 'Travel',
        grand: 'TOTAL BUDGET', notCompletable: 'Not computable (set target/date)',
        exportExcel: 'Export Excel',
        days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        dayTypes: { working: 'Working', saturday: 'Saturday', sunday: 'Sunday' },
        shParam: 'Parameter', shValue: 'Value', shRiepilogo: 'Summary', shPiano: 'Plan',
        shDate: 'Date', shDay: 'Day', shType: 'Type', shTarget: 'Target', shProgr: 'Cumulative', shResidual: 'Remaining',
        shNet: 'Net (€)', shVatAmt: 'VAT (€)', shTotal: 'Total (€)', shTotali: 'TOTALS',
        savedEstimates: 'Saved estimates', load: 'Open', download: 'Download Excel', noSaved: 'No saved estimates',
        flightStatuses: ['Paid', 'To request', 'To pay', 'Refunded', 'Booked'],
        add: 'Add', actions: 'Actions', cancel: 'Cancel', confirmDelete: 'Delete this estimate?',
        newTitle: 'New estimate', editTitle: 'Edit estimate', selectDate: 'Select date', noData: 'No data',
    },
}

const emptyForm = () => ({
    id: null, name: '', total_buses: 0, buses_done: 0, start_date: null,
    target_mon_fri: 2, target_sat: 0, work_on_holidays: false,
    cost_meal: 20, cost_lodging: 90, cost_external: 400, employee_daily_cost: 0,
    num_employees: 0, num_externals: 0, materials: [], flights: [], hotels: [],
})

const euro = (n) => `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100

const BudgetEstimation = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const language = useSelector((state) => state.language?.language) || 'it'
    const t = L[language] || L.en

    const [projects, setProjects] = useState([])
    const [form, setForm] = useState(emptyForm())
    const [saving, setSaving] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)

    const summary = useMemo(() => computeBudget(form), [form])

    const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))

    const fetchProjects = async () => {
        const res = await secureApi.get(GET.GET_BUDGET_PROJECTS)
        if (res?.success) setProjects(res.data || [])
    }

    useEffect(() => {
        verifyToken(history, dispatch)
        fetchProjects()
    }, [history, dispatch])

    const loadProject = (p) => {
        if (!p) { setForm(emptyForm()); return }
        setForm({
            ...emptyForm(), ...p,
            materials: Array.isArray(p.materials) ? p.materials : [],
            flights: Array.isArray(p.flights) ? p.flights : [],
            hotels: Array.isArray(p.hotels) ? p.hotels : [],
        })
    }

    const openNew = () => { setForm(emptyForm()); setDrawerOpen(true) }
    const openEdit = (p) => { loadProject(p); setDrawerOpen(true) }

    const save = async () => {
        if (!form.name?.trim()) { message.error(t.name); return }
        setSaving(true)
        const res = await secureApi.post(POST.SAVE_BUDGET_PROJECT, form)
        setSaving(false)
        if (res?.success) {
            message.success(res.message || 'Saved')
            setDrawerOpen(false)
            fetchProjects()
        } else {
            message.error(res?.message || 'Failed to save')
        }
    }

    const deleteProject = async (id) => {
        const res = await secureApi.post(POST.DELETE_BUDGET_PROJECT, { id })
        if (res?.success) { message.success(res.message || 'Deleted'); fetchProjects() }
        else message.error(res?.message || 'Failed to delete')
    }

    // ---- Excel export (multi-sheet, like the source workbook) ----
    // Works on any project object (a saved estimate from the list, or the
    // current form by default).
    const exportExcel = (project = form) => {
        const s = computeBudget(project)
        const wb = XLSX.utils.book_new()

        // 1) Summary (Riepilogo)
        const summaryRows = [
            [t.shRiepilogo, ''],
            [t.name, project.name],
            [t.totalBuses, project.total_buses],
            [t.busesDone, project.buses_done],
            [t.startDate, project.start_date || ''],
            [t.targetMonFri, project.target_mon_fri],
            [t.targetSat, project.target_sat],
            [t.remaining, s.remainingBuses],
            [t.workingDays, s.workingDays],
            [t.completion, s.completable ? s.completionDate : t.notCompletable],
            ['', ''],
            [t.costMeal, project.cost_meal],
            [t.costLodging, project.cost_lodging],
            [t.costExternal, project.cost_external],
            [t.employeeDaily, project.employee_daily_cost],
            [t.numEmployees, project.num_employees],
            [t.numExternals, project.num_externals],
            ['', ''],
            [t.labor, s.costs.laborCost],
            [t.matTotal, s.costs.materialsTotal],
            [t.travel, s.costs.travelTotal],
            [t.grand, s.costs.grandTotal],
        ]
        const wsSummary = XLSX.utils.aoa_to_sheet([[t.shParam, t.shValue], ...summaryRows])
        wsSummary['!cols'] = [{ wch: 32 }, { wch: 22 }]
        XLSX.utils.book_append_sheet(wb, wsSummary, t.shRiepilogo)

        // 2) Plan (Piano) — day-by-day
        const sched = buildSchedule(project)
        const planAoa = [[t.shDate, t.shDay, t.shType, t.shTarget, t.shProgr, t.shResidual]]
        sched.forEach((r) => planAoa.push([
            r.date, t.days[r.weekday], t.dayTypes[r.dayType] || r.dayType, r.dailyTarget, r.cumulative, r.residual,
        ]))
        const wsPlan = XLSX.utils.aoa_to_sheet(planAoa)
        wsPlan['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 10 }]
        XLSX.utils.book_append_sheet(wb, wsPlan, t.shPiano)

        // 3) Materials
        const matAoa = [[t.type, t.unitPrice, t.qty, t.vat, t.shNet, t.shVatAmt, t.shTotal, t.note]]
        let mNet = 0, mVat = 0
        ;(project.materials || []).forEach((m) => {
            const net = Number(m.unit_price || 0) * Number(m.quantity || 0)
            const vat = net * (Number(m.vat_rate || 0) / 100)
            mNet += net; mVat += vat
            matAoa.push([m.type || '', Number(m.unit_price || 0), Number(m.quantity || 0), Number(m.vat_rate || 0), round2(net), round2(vat), round2(net + vat), m.note || ''])
        })
        matAoa.push([t.shTotali, '', '', '', round2(mNet), round2(mVat), round2(mNet + mVat), ''])
        const wsMat = XLSX.utils.aoa_to_sheet(matAoa)
        wsMat['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 10 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 20 }]
        XLSX.utils.book_append_sheet(wb, wsMat, t.materials)

        // 4) Flights
        const flAoa = [[t.person, t.route, t.amount, t.status, t.note]]
        let flTot = 0
        ;(project.flights || []).forEach((f) => { flTot += Number(f.amount || 0); flAoa.push([f.person || '', f.route || '', Number(f.amount || 0), f.status || '', f.note || '']) })
        flAoa.push([t.shTotali, '', round2(flTot), '', ''])
        const wsFl = XLSX.utils.aoa_to_sheet(flAoa)
        wsFl['!cols'] = [{ wch: 22 }, { wch: 24 }, { wch: 12 }, { wch: 16 }, { wch: 20 }]
        XLSX.utils.book_append_sheet(wb, wsFl, t.flights)

        // 5) Hotels
        const hoAoa = [[t.person, t.arrival, t.nights, t.amount, t.note]]
        let hoTot = 0
        ;(project.hotels || []).forEach((h) => { hoTot += Number(h.amount || 0); hoAoa.push([h.person || '', h.arrival_date || '', Number(h.nights || 0), Number(h.amount || 0), h.note || '']) })
        hoAoa.push([t.shTotali, '', '', round2(hoTot), ''])
        const wsHo = XLSX.utils.aoa_to_sheet(hoAoa)
        wsHo['!cols'] = [{ wch: 22 }, { wch: 14 }, { wch: 8 }, { wch: 12 }, { wch: 20 }]
        XLSX.utils.book_append_sheet(wb, wsHo, t.hotels)

        const safeName = (project.name || 'budget').replace(/[^a-zA-Z0-9_-]/g, '_')
        XLSX.writeFile(wb, `${safeName}_${moment().format('YYYYMMDD')}.xlsx`)
    }

    // ---- editable line-item helpers ----
    const addItem = (key, blank) => set(key, [...(form[key] || []), blank])
    const updItem = (key, idx, field, value) =>
        set(key, form[key].map((r, i) => (i === idx ? { ...r, [field]: value } : r)))
    const updItemFields = (key, idx, fields) =>
        set(key, form[key].map((r, i) => (i === idx ? { ...r, ...fields } : r)))
    const delItem = (key, idx) => set(key, form[key].filter((_, i) => i !== idx))

    // Material type: pick from the shared units library OR type a custom name.
    // Picking a known unit auto-fills its default price.
    const unitOptions = unitLibrary.map((u) => ({ value: u.name, label: `${u.name} (€ ${u.price})` }))
    const unitTypeCell = (idx) => (
        <AutoComplete
            style={{ width: '100%' }}
            value={form.materials[idx]?.type}
            options={unitOptions}
            placeholder={t.type}
            filterOption={(input, option) => String(option?.value || '').toLowerCase().includes(String(input).toLowerCase())}
            onChange={(v) => updItem('materials', idx, 'type', v)}
            onSelect={(v) => {
                const found = unitLibrary.find((u) => u.name === v)
                if (found) updItemFields('materials', idx, { type: v, unit_price: found.price })
                else updItem('materials', idx, 'type', v)
            }}
        />
    )

    const numCell = (key, idx, field) => (
        <InputNumber style={{ width: '100%' }} value={form[key][idx][field]}
            onChange={(v) => updItem(key, idx, field, v)} />
    )
    const txtCell = (key, idx, field) => (
        <Input value={form[key][idx][field]} onChange={(e) => updItem(key, idx, field, e.target.value)} />
    )
    const dateCell = (key, idx, field) => (
        <DatePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            placeholder={t.selectDate}
            value={form[key][idx][field] ? moment(form[key][idx][field], 'YYYY-MM-DD') : null}
            onChange={(d) => updItem(key, idx, field, d ? d.format('YYYY-MM-DD') : null)}
        />
    )
    const delCell = (key, idx) => (
        <Button danger type="text" icon={<DeleteOutlined />} onClick={() => delItem(key, idx)} />
    )
    // Flight status: pick a common status OR type a custom one.
    const statusCell = (idx) => (
        <AutoComplete
            style={{ width: '100%' }}
            value={form.flights[idx]?.status}
            options={(t.flightStatuses || []).map((v) => ({ value: v }))}
            placeholder={t.status}
            filterOption={(input, option) => String(option?.value || '').toLowerCase().includes(String(input).toLowerCase())}
            onChange={(v) => updItem('flights', idx, 'status', v)}
        />
    )

    const materialCols = [
        { title: t.type, render: (_, __, i) => unitTypeCell(i) },
        { title: t.unitPrice, width: 120, render: (_, __, i) => numCell('materials', i, 'unit_price') },
        { title: t.qty, width: 110, render: (_, __, i) => numCell('materials', i, 'quantity') },
        { title: t.vat, width: 90, render: (_, __, i) => numCell('materials', i, 'vat_rate') },
        { title: '', width: 50, render: (_, __, i) => delCell('materials', i) },
    ]
    const flightCols = [
        { title: t.person, render: (_, __, i) => txtCell('flights', i, 'person') },
        { title: t.route, render: (_, __, i) => txtCell('flights', i, 'route') },
        { title: t.amount, width: 120, render: (_, __, i) => numCell('flights', i, 'amount') },
        { title: t.status, width: 150, render: (_, __, i) => statusCell(i) },
        { title: '', width: 50, render: (_, __, i) => delCell('flights', i) },
    ]
    const hotelCols = [
        { title: t.person, render: (_, __, i) => txtCell('hotels', i, 'person') },
        { title: t.arrival, width: 170, render: (_, __, i) => dateCell('hotels', i, 'arrival_date') },
        { title: t.nights, width: 90, render: (_, __, i) => numCell('hotels', i, 'nights') },
        { title: t.amount, width: 120, render: (_, __, i) => numCell('hotels', i, 'amount') },
        { title: '', width: 50, render: (_, __, i) => delCell('hotels', i) },
    ]

    const numField = (key, label) => (
        <Col xs={12} md={8} lg={6}>
            <Text type="secondary">{label}</Text>
            <InputNumber style={{ width: '100%' }} value={form[key]} onChange={(v) => set(key, v)} />
        </Col>
    )

    const formBody = (
        <Row gutter={16}>
            <Col xs={24} lg={16}>
                    <Card title={t.params} size="small" style={{ marginBottom: 16 }}>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={12}>
                                <Text type="secondary">{t.name}</Text>
                                <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
                            </Col>
                            <Col xs={12} md={6}>
                                <Text type="secondary">{t.startDate}</Text>
                                <DatePicker style={{ width: '100%' }}
                                    placeholder={t.selectDate}
                                    value={form.start_date ? moment(form.start_date, 'YYYY-MM-DD') : null}
                                    onChange={(d) => set('start_date', d ? d.format('YYYY-MM-DD') : null)} />
                            </Col>
                            <Col xs={12} md={6}>
                                <Text type="secondary">{t.workHolidays}</Text><br />
                                <Switch checked={!!form.work_on_holidays} onChange={(v) => set('work_on_holidays', v)} />
                            </Col>
                            {numField('total_buses', t.totalBuses)}
                            {numField('buses_done', t.busesDone)}
                            {numField('target_mon_fri', t.targetMonFri)}
                            {numField('target_sat', t.targetSat)}
                        </Row>
                        <Divider orientation="left" plain>{t.costs}</Divider>
                        <Row gutter={[12, 12]}>
                            {numField('cost_meal', t.costMeal)}
                            {numField('cost_lodging', t.costLodging)}
                            {numField('cost_external', t.costExternal)}
                            {numField('employee_daily_cost', t.employeeDaily)}
                        </Row>
                        <Divider orientation="left" plain>{t.team}</Divider>
                        <Row gutter={[12, 12]}>
                            {numField('num_employees', t.numEmployees)}
                            {numField('num_externals', t.numExternals)}
                        </Row>
                    </Card>

                    <Card title={t.materials} size="small" style={{ marginBottom: 16 }}
                        extra={<Button size="small" icon={<PlusOutlined />}
                            onClick={() => addItem('materials', { type: '', unit_price: 0, quantity: 0, vat_rate: 22 })}>{t.addRow}</Button>}>
                        <Table size="small" pagination={false} rowKey={(_, i) => i}
                            columns={materialCols} dataSource={form.materials}
                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t.noData} /> }} />
                    </Card>

                    <Card title={t.flights} size="small" style={{ marginBottom: 16 }}
                        extra={<Button size="small" icon={<PlusOutlined />}
                            onClick={() => addItem('flights', { person: '', route: '', amount: 0, status: '' })}>{t.addRow}</Button>}>
                        <Table size="small" pagination={false} rowKey={(_, i) => i}
                            columns={flightCols} dataSource={form.flights}
                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t.noData} /> }} />
                    </Card>

                    <Card title={t.hotels} size="small" style={{ marginBottom: 16 }}
                        extra={<Button size="small" icon={<PlusOutlined />}
                            onClick={() => addItem('hotels', { person: '', arrival_date: '', nights: 0, amount: 0 })}>{t.addRow}</Button>}>
                        <Table size="small" pagination={false} rowKey={(_, i) => i}
                            columns={hotelCols} dataSource={form.hotels}
                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t.noData} /> }} />
                    </Card>
                </Col>

                <Col xs={24} lg={8}>
                    <Card title={t.summary} size="small" style={{ position: 'sticky', top: 16 }}>
                        <Row justify="space-between"><Text>{t.remaining}</Text><Text strong>{summary.remainingBuses}</Text></Row>
                        <Row justify="space-between"><Text>{t.workingDays}</Text><Text strong>{summary.workingDays}</Text></Row>
                        <Row justify="space-between">
                            <Text>{t.completion}</Text>
                            <Text strong>{summary.completable ? summary.completionDate : t.notCompletable}</Text>
                        </Row>
                        <Divider style={{ margin: '12px 0' }} />
                        <Row justify="space-between"><Text>{t.labor}</Text><Text>{euro(summary.costs.laborCost)}</Text></Row>
                        <Row justify="space-between"><Text>{t.matTotal}</Text><Text>{euro(summary.costs.materialsTotal)}</Text></Row>
                        <Row justify="space-between"><Text>{t.travel}</Text><Text>{euro(summary.costs.travelTotal)}</Text></Row>
                        <Divider style={{ margin: '12px 0' }} />
                        <Row justify="space-between" align="middle">
                            <Text strong style={{ fontSize: 16 }}>{t.grand}</Text>
                            <Text strong style={{ fontSize: 20, color: THEME_COLOR }}>{euro(summary.costs.grandTotal)}</Text>
                        </Row>
                    </Card>
                </Col>
            </Row>
    )

    return (
        <div style={{ padding: 16 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>{t.title}</Title>
                    <Text type="secondary">{t.subtitle}</Text>
                </div>
                <Button className="export-btn" type="primary" icon={<PlusOutlined />} onClick={openNew}
                    style={{ background: THEME_COLOR, borderColor: THEME_COLOR }}>{t.newTitle}</Button>
            </Row>

            <Card size="small">
                <Table
                    rowKey="id" dataSource={projects} pagination={false}
                    locale={{ emptyText: <Empty description={t.noSaved} /> }}
                    columns={[
                        { title: t.name, dataIndex: 'name' },
                        { title: t.grand, align: 'right', width: 180, render: (_, p) => <Text strong>{euro(p.summary?.costs?.grandTotal)}</Text> },
                        { title: t.completion, align: 'center', width: 150, render: (_, p) => (p.summary?.completable ? p.summary?.completionDate : '—') },
                        {
                            title: t.actions, align: 'right', width: 150, render: (_, p) => (
                                <Space>
                                    <Tooltip title={t.editTitle}>
                                        <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => openEdit(p)} />
                                    </Tooltip>
                                    <Tooltip title={t.download}>
                                        <Button type="text" icon={<FileExcelOutlined style={{ color: '#217346' }} />} onClick={() => exportExcel(p)} />
                                    </Tooltip>
                                    <Popconfirm
                                        title={t.confirmDelete}
                                        placement="topRight"
                                        overlayStyle={{ zIndex: 1100 }}
                                        onConfirm={() => deleteProject(p.id)}
                                    >
                                        <Button type="text" danger icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                </Space>
                            ),
                        },
                    ]}
                />
            </Card>

            <Drawer
                title={form.id ? t.editTitle : t.newTitle}
                width="92%"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setDrawerOpen(false)}>{t.cancel}</Button>
                            <Button onClick={() => exportExcel()} icon={<FileExcelOutlined />}
                                style={{ color: '#217346', borderColor: '#217346' }}>{t.exportExcel}</Button>
                            <Button type="primary" loading={saving} onClick={save} icon={<SaveOutlined />}
                                style={{ background: THEME_COLOR, borderColor: THEME_COLOR }}>{t.save}</Button>
                        </Space>
                    </div>
                }
            >
                {formBody}
            </Drawer>
        </div>
    )
}

export default BudgetEstimation
